import React, {useEffect, useMemo, useRef} from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { Billboard, Text } from '@react-three/drei';
import VegetableCharacterFactory from './VegetableCharacterFactory';
import { vegetableColors } from '../constants.js';
import {useWebSocket} from "../../contexts/WebSocketContext.jsx";

const POSITION_LERP_FACTOR = 0.2;
const ROTATION_LERP_FACTOR = 0.1;

const OtherPlayer = ({ playerId, playerState }) => {
    const { getServerTime, avgPingTime } = useWebSocket();
    const meshRef = useRef();
    const targetPosition = useRef(new THREE.Vector3());
    const currentVelocity = useRef(new THREE.Vector3());
    const targetRotation = useRef(new THREE.Euler());
    const lastUpdateTime = useRef(Date.now());
    const lastPositionTime = useRef(0);

    // Extract vegetable type from player state
    const vegetableType = playerState?.vegetableType || 'default';
    const isWalking = playerState?.isWalking || false;

    // Compute decayed glow for this player
    const computeGlow = () => {
        const base = typeof playerState?.glowLevel === 'number' ? playerState.glowLevel : 0;
        const updated = typeof playerState?.glowUpdatedMs === 'number' ? playerState.glowUpdatedMs : Date.now();
        const now = Date.now();
        const elapsedSec = Math.max(0, (now - updated) / 1000);
        const decayPerSecond = 1 / 300; // keep in sync with server
        const g = base - decayPerSecond * elapsedSec;
        return Math.max(0, g);
    };

    // Update target position when playerState changes
    useEffect(() => {
        if (!playerState || !playerState.position) return;

        // Store the timestamp of this position update
        lastPositionTime.current = playerState.timestamp || Date.now();

        // Update target position
        targetPosition.current.set(
            playerState.position[0],
            playerState.position[1] - 1, // Subtract 1 to place on ground
            playerState.position[2]
        );

        // Store velocity for prediction
        if (playerState.velocity) {
            currentVelocity.current.set(
                playerState.velocity[0],
                playerState.velocity[1],
                playerState.velocity[2]
            );
        } else {
            currentVelocity.current.set(0, 0, 0);
        }

        // Update rotation
        if (playerState.rotation) {
            targetRotation.current.set(
                playerState.rotation[0],
                playerState.rotation[1],
                playerState.rotation[2]
            );
        }

        lastUpdateTime.current = Date.now();
    }, [playerState]);

    // Smoothly interpolate to target position, with prediction based on velocity
    useFrame(() => {
        if (!meshRef.current || !playerState) return;

        const now = Date.now();
        const timeSinceUpdate = (now - lastUpdateTime.current) / 1000; // Convert to seconds

        // For very stale updates (over 3 seconds), fade out interpolation
        const staleFactor = Math.max(0, 1 - ((now - lastUpdateTime.current) / 3000));
        const adjustedLerpFactor = POSITION_LERP_FACTOR * staleFactor;

        // Calculate predicted position based on velocity and elapsed time
        const predictedPosition = new THREE.Vector3(
            targetPosition.current.x + (currentVelocity.current.x * timeSinceUpdate),
            targetPosition.current.y + (currentVelocity.current.y * timeSinceUpdate),
            targetPosition.current.z + (currentVelocity.current.z * timeSinceUpdate)
        );

        // Lerp current position toward predicted position
        meshRef.current.position.lerp(predictedPosition, adjustedLerpFactor);

        // Slerp rotation (smoother for rotation)
        const currentRotation = meshRef.current.rotation;
        currentRotation.y = THREE.MathUtils.lerp(
            currentRotation.y,
            targetRotation.current.y,
            ROTATION_LERP_FACTOR * staleFactor
        );
    });

    // If no player state, don't render
    if (!playerState || !playerState.position) {
        return null;
    }

    const displayName = (playerState.name || '').trim();
    const labelHeight = 1.6 + Math.max(0, (playerState.scale || 0.25) - 0.25) * 0.8;

    return (
        <group ref={meshRef} position={[0, 0, 0]}>
            {/* Glow light for other player based on decayed glow */}
            <pointLight
                position={[0, 1, 0]}
                color={'#ffffaa'}
                intensity={Math.min(2.0, computeGlow() * 0.2)}
                distance={5}
            />
            <VegetableCharacterFactory
                vegetableType={vegetableType || 'default'}
                position={[0, 0, 0]} // Position will be handled by the parent group
                rotation={[0, 0, 0]} // Rotation will be handled by the parent group
                isWalking={isWalking}
                scale={playerState.scale || 0.25}
            />
            {/* Floating name label */}
            {displayName && (
                <Billboard position={[0, labelHeight, 0]} follow={true} lockX={false} lockY={false} lockZ={false}>
                    <Text
                        fontSize={0.25}
                        color="#ffffff"
                        anchorX="center"
                        anchorY="bottom"
                        outlineWidth={0.01}
                        outlineColor="#000000"
                        maxWidth={4}
                    >
                        {displayName}
                    </Text>
                </Billboard>
            )}
        </group>
    );
};

export default OtherPlayer;
